import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, finalize, take } from 'rxjs/operators';
import {
  SaasContainerActionDialogComponent,
  SaasContainerActionDialogData
} from './saas-container-action-dialog.component';
import {
  SaasRouteDialogComponent,
  SaasRouteDialogData,
  SaasRouteDialogResult
} from './saas-route-dialog.component';
import {
  TableCellDirective,
  TableColumn,
  TableEmptyState,
  TableFilter,
  TableInspector,
  TablePageAction,
  TablePageActionEvent,
  TablePageComponent,
  TableRowAction,
  TableRowActionEvent,
  TableSummaryItem,
  TableTone
} from '../table/table-page.component';
import {
  SaasContainerAction,
  SaasInstance,
  SaasInstanceDiscoveryService
} from './saas-instance-discovery.service';

@Component({
  selector: 'app-saas-stack-containers',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, MatSnackBarModule, TableCellDirective, TablePageComponent],
  templateUrl: './saas-stack-containers.component.html',
  styleUrls: ['./saas-service-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SaasStackContainersComponent implements OnInit {
  actions: readonly TablePageAction[] = [
    { id: 'back', label: 'Docker-Stacks', icon: 'arrow_back', appearance: 'basic' }
  ];

  readonly columns: readonly TableColumn<SaasInstance>[] = [
    { key: 'containerName', label: 'Container', secondaryValue: 'containerId' },
    { key: 'serviceTypeLabel', label: 'Service-Typ' },
    { key: 'backendIp', label: 'Backend-IP', emptyText: 'Nicht geliefert' },
    { key: 'backendPort', label: 'Backend-Port', emptyText: '—', align: 'end' },
    { key: 'baseUrl', label: 'Base URL', emptyText: 'Nicht geliefert', sortable: false },
    { key: 'createdAt', label: 'Erstellt', type: 'datetime', emptyText: 'Nicht geliefert' },
    { key: 'statusLabel', label: 'Status', type: 'badge', tone: row => this.statusTone(row) }
  ];

  readonly rowActions: readonly TableRowAction<SaasInstance>[] = [
    {
      id: 'start',
      label: 'Container starten',
      icon: 'play_arrow',
      hidden: row => row.status !== 'STOPPED',
      disabled: () => this.actionContainerId !== null || this.routeContainerId !== null,
      loading: row => this.actionContainerId === row.containerId
    },
    {
      id: 'stop',
      label: 'Container stoppen',
      icon: 'stop',
      tone: 'warn',
      hidden: row => row.status !== 'ACTIVE',
      disabled: () => this.actionContainerId !== null || this.routeContainerId !== null,
      loading: row => this.actionContainerId === row.containerId
    },
    {
      id: 'route',
      label: 'Route konfigurieren',
      icon: 'route',
      appearance: 'basic',
      disabled: () => this.actionContainerId !== null || this.routeContainerId !== null,
      loading: row => this.routeContainerId === row.containerId
    }
  ];

  readonly summaryItems: readonly TableSummaryItem<SaasInstance>[] = [
    { label: 'Container', value: rows => rows.length, icon: 'dns' },
    { label: 'Aktiv', value: rows => rows.filter(row => row.status === 'ACTIVE').length, icon: 'play_circle' },
    { label: 'Gestoppt', value: rows => rows.filter(row => row.status === 'STOPPED').length, icon: 'stop_circle' }
  ];

  readonly emptyState: TableEmptyState = {
    icon: 'cloud_off',
    title: 'Keine Container entdeckt',
    description: 'Die n8n-Discovery hat für diesen Docker-Stack keine Container gemeldet.'
  };

  readonly inspector: TableInspector<SaasInstance> = {
    ariaLabel: 'Details des ausgewählten Containers',
    title: 'containerName',
    subtitle: 'serviceTypeLabel',
    description: row => `Docker-Stack ${row.stack}`,
    icon: row => this.serviceIcon(row.serviceType),
    tone: row => this.statusTone(row),
    fields: [
      { label: 'Stack', value: 'stack', monospace: true },
      { label: 'Container-ID', value: 'containerId', monospace: true },
      { label: 'Organisation', value: 'organisation', emptyText: 'Nicht geliefert' },
      { label: 'Backend-IP', value: 'backendIp', emptyText: 'Nicht geliefert', monospace: true },
      { label: 'Backend-Port', value: 'backendPort', emptyText: 'Nicht veröffentlicht', monospace: true },
      { label: 'Base URL', value: 'baseUrl', emptyText: 'Nicht geliefert' },
      { label: 'Erstellt', value: 'createdAt', type: 'datetime', emptyText: 'Nicht geliefert' },
      { label: 'Docker-Status', value: 'dockerStatus' }
    ],
    actions: this.rowActions,
  };

  readonly stackName: string;
  containers: SaasInstance[] = [];
  filters: readonly TableFilter<SaasInstance>[] = [];
  selectedContainer: SaasInstance | null = null;
  loading = true;
  refreshing = false;
  errorMessage = '';
  lastDiscovery: Date | null = null;
  actionContainerId: string | null = null;
  routeContainerId: string | null = null;

  constructor(
    route: ActivatedRoute,
    private readonly discoveryService: SaasInstanceDiscoveryService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router
  ) {
    this.stackName = route.snapshot.paramMap.get('stack') ?? '';
  }

  ngOnInit(): void {
    this.loadContainers(false);
  }

  get description(): string {
    const timestamp = this.lastDiscovery
      ? ` Letzte Discovery: ${new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(this.lastDiscovery)} Uhr.`
      : '';
    return `Container im Docker-Stack ${this.stackName}.${timestamp}`;
  }

  refresh(): void {
    if (this.loading || this.refreshing) return;
    this.loadContainers(true);
  }

  retry(): void {
    this.loadContainers(false);
  }

  handlePageAction(event: TablePageActionEvent): void {
    if (event.actionId === 'back') void this.router.navigate(['/profil/saas-services']);
  }

  handleRowAction(event: TableRowActionEvent<SaasInstance>): void {
    if (event.actionId === 'route') {
      this.openRouteDialog(event.row);
      return;
    }
    if (event.actionId !== 'start' && event.actionId !== 'stop') return;
    const action: SaasContainerAction = event.actionId;
    const data: SaasContainerActionDialogData = {
      action,
      containerName: event.row.containerName
    };

    this.dialog.open(SaasContainerActionDialogComponent, { width: '28rem', data }).afterClosed().pipe(
      take(1),
      filter((token): token is string => typeof token === 'string' && token.trim().length > 0)
    ).subscribe(token => this.executeContainerAction(action, event.row, token));
  }

  private openRouteDialog(container: SaasInstance): void {
    const data: SaasRouteDialogData = {
      stack: container.stack,
      containerName: container.containerName,
      customerId: this.defaultCustomerId(container),
      backendIp: container.backendIp,
      backendPort: container.backendPort
    };

    this.dialog.open(SaasRouteDialogComponent, {
      width: '38rem',
      maxWidth: 'calc(100vw - 2rem)',
      data
    }).afterClosed().pipe(
      take(1),
      filter((result): result is SaasRouteDialogResult => Boolean(result))
    ).subscribe(result => this.executeRouteUpsert(container, result));
  }

  externalUrl(instance: SaasInstance): string | null {
    if (!instance.baseUrl) return null;
    try {
      const url = new URL(/^https?:\/\//i.test(instance.baseUrl) ? instance.baseUrl : `https://${instance.baseUrl}`);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
      return this.isLocalAddress(url.hostname) ? null : url.toString();
    } catch {
      return null;
    }
  }

  private isLocalAddress(hostname: string): boolean {
    const host = hostname.toLowerCase().replace(/^\[|\]$/g, '').replace(/\.$/, '');
    if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local') || host === 'host.docker.internal') {
      return true;
    }

    if (host === '::' || host === '::1' || /^[fF][c-d]/.test(host) || /^fe[89ab]/i.test(host)) {
      return true;
    }

    const ipv4Host = host.startsWith('::ffff:') ? host.slice(7) : host;
    const octets = ipv4Host.split('.').map(part => Number(part));
    if (octets.length !== 4 || octets.some(octet => !Number.isInteger(octet) || octet < 0 || octet > 255)) {
      return false;
    }

    const [first, second] = octets;
    return first === 0
      || first === 10
      || first === 127
      || (first === 100 && second >= 64 && second <= 127)
      || (first === 169 && second === 254)
      || (first === 172 && second >= 16 && second <= 31)
      || (first === 192 && second === 168)
      || (first === 198 && (second === 18 || second === 19));
  }

  private loadContainers(isRefresh: boolean): void {
    this.errorMessage = '';
    this.loading = !isRefresh;
    this.refreshing = isRefresh;

    this.discoveryService.discoverContainers(this.stackName).pipe(
      finalize(() => {
        this.loading = false;
        this.refreshing = false;
        this.changeDetectorRef.markForCheck();
      })
    ).subscribe({
      next: containers => {
        this.containers = containers;
        this.filters = this.buildFilters(containers);
        this.lastDiscovery = new Date();
        if (isRefresh) {
          this.snackBar.open(`${containers.length} Container aktualisiert.`, 'Schließen', { duration: 3500 });
        }
      },
      error: error => this.errorMessage = this.errorText(error)
    });
  }

  private executeContainerAction(action: SaasContainerAction, container: SaasInstance, pb40Token: string): void {
    if (this.actionContainerId !== null || this.routeContainerId !== null) return;
    this.actionContainerId = container.containerId;
    this.changeDetectorRef.markForCheck();

    this.discoveryService.actionContainer(action, container.containerId, pb40Token).pipe(
      finalize(() => {
        this.actionContainerId = null;
        this.changeDetectorRef.markForCheck();
      })
    ).subscribe({
      next: () => {
        const message = action === 'start' ? 'Container wurde gestartet.' : 'Container wurde gestoppt.';
        this.snackBar.open(message, 'Schließen', { duration: 3500 });
        this.loadContainers(true);
      },
      error: error => this.snackBar.open(this.errorText(error), 'Schließen', { duration: 6000 })
    });
  }

  private executeRouteUpsert(container: SaasInstance, result: SaasRouteDialogResult): void {
    if (this.actionContainerId !== null || this.routeContainerId !== null) return;
    this.routeContainerId = container.containerId;
    this.changeDetectorRef.markForCheck();

    this.discoveryService.upsertRoute({
      customerId: result.customerId,
      backendIp: result.backendIp,
      backendPort: result.backendPort,
      pb40Token: result.pb40Token
    }).pipe(
      finalize(() => {
        this.routeContainerId = null;
        this.changeDetectorRef.markForCheck();
      })
    ).subscribe({
      next: () => {
        this.snackBar.open(`Route für ${result.customerId} wurde gespeichert.`, 'Schließen', { duration: 4000 });
        this.loadContainers(true);
      },
      error: error => this.snackBar.open(this.errorText(error), 'Schließen', { duration: 6000 })
    });
  }

  private defaultCustomerId(container: SaasInstance): string {
    const source = container.organisation?.trim() || container.stack;
    const normalised = source
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 63)
      .replace(/-+$/g, '');
    return normalised || container.stack.toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 63);
  }

  private buildFilters(containers: readonly SaasInstance[]): readonly TableFilter<SaasInstance>[] {
    const serviceTypes = [...new Map(containers.map(container => [container.serviceType, container.serviceTypeLabel])).entries()]
      .sort((left, right) => left[1].localeCompare(right[1], 'de'));
    return [
      {
        id: 'serviceType',
        label: 'Service-Typ',
        options: serviceTypes.map(([value, label]) => ({ value, label })),
        allLabel: 'Alle Services'
      },
      {
        id: 'status',
        label: 'Status',
        options: [
          { value: 'ACTIVE', label: 'Aktiv' },
          { value: 'STOPPED', label: 'Gestoppt' },
          { value: 'UNKNOWN', label: 'Unbekannt' }
        ],
        allLabel: 'Alle Status'
      }
    ];
  }

  private statusTone(instance: SaasInstance): TableTone {
    if (instance.status === 'ACTIVE') return 'success';
    if (instance.status === 'STOPPED') return 'warn';
    return 'neutral';
  }

  private serviceIcon(type: string): string {
    const icons: Record<string, string> = {
      WALLET: 'account_balance_wallet',
      CONNECTOR: 'hub',
      IDENTITY_HUB: 'badge',
      ISSUER: 'verified',
      KEYSTORE: 'key',
      DATABASE: 'database'
    };
    return icons[type] ?? 'dns';
  }

  private errorText(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string' && error.error.trim()) return error.error;
      if (error.error && typeof error.error === 'object' && typeof error.error.message === 'string') {
        return error.error.message;
      }
      if (error.status === 0) {
        return 'Die n8n-Discovery ist nicht erreichbar. Prüfen Sie Workflow-Testmodus, Netzwerk und Proxy-Konfiguration.';
      }
    }
    if (error instanceof Error && error.message) return error.message;
    return 'Die Container konnten nicht geladen werden.';
  }
}
